import React, { FunctionComponent, useRef, useState } from 'react';

import { css } from '@emotion/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';

import Button from '@components/Common/Button';
import Form from '@components/Common/Form';
import FormRow from '@components/Common/FormRow';
import FormSection from '@components/Common/FormSection';
import Input from '@components/Common/Input';
import LoadingSpinner from '@components/Common/LoadingSpinner';
import ConfirmModal from '@components/Modals/ConfirmModal';

import { PasswordChangeFormInputs } from '@type/mypage';

import { usePasswordChange } from '@hooks/api/mypage/useMypageAPIs';

import { palette } from '@styles/theme';

import { auth } from '@constants/state';
import { passwordValidateOptions } from '@constants/validation';

/** 사용자 설정 > 비밀번호 변경 */
const PasswordChangeForm: FunctionComponent = () => {
  /** 사용자 정보 State 호출 */
  const { email } = useRecoilValue(auth);

  /** Confirm Modal State */
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);

  /** react-hook-form init */
  const {
    control,
    watch,
    reset,
    formState: { errors },
    handleSubmit,
  } = useForm<PasswordChangeFormInputs>();

  /** 비밀번호 변경하기 API 호출 */
  const { mutate, isLoading } = usePasswordChange(setConfirmModalOpen, reset);

  const password = useRef({});
  password.current = watch('newPassword', '');

  /** 비밀번호확인 유효성 체크 */
  const passwordConfirmValidateOptions = {
    required: '비밀번호 확인값을 입력해 주세요.',
    validate: (value: any) =>
      value === password.current || '비밀번호가 일치하지 않습니다.',
  };

  /** 변경하기 버튼 클릭 시 Handler */
  const onSubmit: SubmitHandler<PasswordChangeFormInputs> = async () => {
    try {
      setConfirmModalOpen(true);
    } catch (error: any) {
      console.log('ERROR_NEW_FUND');
    }
  };

  /** 비밀번호 변경하기 (최종 컨펌창 승인 시) */
  const handleConfirm: SubmitHandler<PasswordChangeFormInputs> = async ({
    password,
    newPassword,
  }) => {
    try {
      mutate({
        password,
        newPassword,
      });
    } catch (error: any) {
      console.log('ERROR_NEW_FUND');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <form css={container} onSubmit={handleSubmit(onSubmit)}>
        <Form>
          <FormRow>
            <FormSection label="이메일">{email}</FormSection>
          </FormRow>
          <FormRow>
            <FormSection label="현재 비밀번호">
              <Input
                name="password"
                type="password"
                control={control}
                rules={{ required: '현재 비밀번호를 입력해 주세요.' }}
                errors={errors}
                placeHolder="현재 비밀번호를 입력해 주세요."
              />
            </FormSection>
          </FormRow>
          <FormRow>
            <FormSection label="새 비밀번호">
              <Input
                name="newPassword"
                type="password"
                control={control}
                rules={passwordValidateOptions}
                errors={errors}
                placeHolder="새 비밀번호를 입력해 주세요."
              />
            </FormSection>
          </FormRow>
          <FormRow>
            <FormSection label="비밀번호 확인">
              <Input
                name="newPasswordCheck"
                type="password"
                control={control}
                rules={passwordConfirmValidateOptions}
                errors={errors}
                placeHolder="비밀번호를 확인해주세요."
              />
            </FormSection>
          </FormRow>
        </Form>

        <div css={btnWrap}>
          <Button
            type="submit"
            label="비밀번호 변경하기"
            handleClick={handleSubmit(onSubmit)}
          />
        </div>
      </form>

      {/* Confirm Modal */}
      {confirmModalOpen && (
        <ConfirmModal
          title="변경하시겠습니까?"
          buttonText={['취소하기', '변경하기']}
          open={confirmModalOpen}
          handleOpen={() => setConfirmModalOpen(false)}
          handleCancel={() => setConfirmModalOpen(false)}
          handleConfirm={handleSubmit(handleConfirm)}
        />
      )}
    </>
  );
};

export default PasswordChangeForm;

const container = css`
  width: 50%;
`;

const btnWrap = css`
  display: flex;
  margin-top: 20px;
  justify-content: flex-end;
  button {
    background-color: ${palette.primaryColor};
    &:hover,
    &:active {
      background-color: ${palette.primaryColor};
      opacity: 0.8;
    }
  }
`;
